/**
 * Follow-Up Card Component (Presentation)
 *
 * Individual follow-up card in the queue panel follow-ups tab.
 */

import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback';
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';

import { PersonDisplay } from '@/shared/components';
import type { CallFollowUpModel } from '@/modules/call-follow-up';

interface FollowUpCardProps {
  followUp: CallFollowUpModel;
  isActive?: boolean;
  onClick?: () => void;
}

export function FollowUpCard({ followUp, isActive = false, onClick }: FollowUpCardProps) {
  const theme = useTheme();

  const isIncoming = followUp.call_queue_type === 'incoming';
  // V2 `caller` is the OUTGOING participant (agent) and `callees` the INCOMING participants (customer) for both
  // directions per backend source at 92e2f23 (inbound not yet confirmed against a real staging row).
  // Fall back to the caller so the card never renders empty.
  const displayPerson = followUp.callee ?? followUp.caller;

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: 'flex',
        width: '100%',
        textAlign: 'left',
        p: 1.5,
        bgcolor: isActive ? alpha(theme.palette.primary.main, 0.08) : 'background.default',
        border: 1,
        borderColor: isActive ? 'primary.main' : 'divider',
        borderRadius: 2,
        transition: 'all 0.2s',
        '&:hover': {
          bgcolor: isActive ? alpha(theme.palette.primary.main, 0.12) : 'action.hover',
        },
      }}
    >
      {/* Call Direction Icon */}
      <Box
        sx={{
          color: isIncoming ? 'info.main' : 'success.main',
          mr: 1.5,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {isIncoming ? <PhoneCallbackIcon fontSize="small" /> : <PhoneForwardedIcon fontSize="small" />}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          {followUp.assigned_tier && (
            <Chip
              label={followUp.assigned_tier}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.625rem',
                fontWeight: 700,
                bgcolor: alpha(theme.palette.secondary.main, 0.15),
                color: 'secondary.main',
              }}
            />
          )}
        </Box>

        <Box sx={{ mb: 0.5 }}>
          <PersonDisplay person={displayPerson} variant="compact" avatarSize={28} fallbackLabel="Unknown" />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {followUp.loan_id && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {followUp.loan_id}
            </Typography>
          )}
        </Box>

        {followUp.call_outcome && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {followUp.call_outcome}
          </Typography>
        )}
      </Box>
    </ButtonBase>
  );
}
